import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Header from "../components/Header"; // ⬅️ adjust path if different
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
  Typography,
  Divider,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import MUIDataTable from "mui-datatables";
import { toast } from "react-toastify";

/**
 * SroTimeManager.jsx
 * - Header at top
 * - “New Request” button opens a centered popup (MUI Dialog)
 * - Update SRO time (bulk or selected SROs) with optional file + remark
 * - Logs section stays on main page
 */

// ===== API base (local dev) =====
const API_BASE = "https://autoreg.eregistrationukgov.in"; // no trailing slash

// Extract just the filename from a path (works for / and \)
const toFileName = (p) => {
  if (!p) return "";
  const parts = String(p).split(/[\\/]/);
  return parts[parts.length - 1];
};

// Build sroview URL with URL-encoded file name
const sroViewUrl = (p) => {
  const fname = toFileName(p);
  if (!fname) return "";
  return `${API_BASE}/api/Files/sroview/${encodeURIComponent(fname)}`;
};

const hourOptions = Array.from({ length: 24 }, (_, i) => i);
const minOptions = Array.from({ length: 60 }, (_, i) => i);

export default function SroTimeManager({ updatedBy }) {
  // ===== Masters =====
  const [districts, setDistricts] = useState([]);
  const [sros, setSros] = useState([]);

  // ===== Modal open/close =====
  const [open, setOpen] = useState(false);

  // ===== Update form =====
  const [form, setForm] = useState({
    dcode: "",
    srocodes: [], // multi-select for SROs; empty => ALL SROs in district
    hour: 10,
    min: 30,
    remark: "",
    file: null,
  });

  // ===== Logs filter =====
  const [flt, setFlt] = useState({
    dcode: "",
    srocode: "",
    fromDate: null,
    toDate: null,
  });

  // ===== Logs data & state =====
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fltSros, setFltSros] = useState([]);

  // ───────────────────────────────── Masters ─────────────────────────────────
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/Auth/get_District_Mas`
        );
        setDistricts(data?.data ?? []);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load districts");
      }
    };
    loadDistricts();
  }, []);

  // Load SROs for the UPDATE form when form.dcode changes
  useEffect(() => {
    const loadSros = async () => {
      if (!form.dcode) {
        setSros([]);
        return;
      }
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/Auth/get_sro_master_data`,
          {
            params: { dcode: form.dcode },
          }
        );
        setSros(data?.data ?? []);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load SROs");
      }
    };
    loadSros();
    setForm((p) => ({ ...p, srocodes: [] })); // clear selection when district changes
  }, [form.dcode]);

  // Load SROs for the LOGS FILTER when flt.dcode changes
  useEffect(() => {
    const loadFltSros = async () => {
      if (!flt.dcode) {
        setFltSros([]);
        return;
      }
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/Auth/get_sro_master_data`,
          {
            params: { dcode: flt.dcode },
          }
        );
        setFltSros(data?.data ?? []);
      } catch {
        setFltSros([]);
      }
    };
    loadFltSros();
    setFlt((p) => ({ ...p, srocode: "" }));
  }, [flt.dcode]);

  // ─────────────────────────────── Update Submit ─────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!form.dcode) return toast.error("Select a district");
    if (form.hour < 0 || form.hour > 23 || form.min < 0 || form.min > 59) {
      return toast.error("Hour 0–23 and Min 0–59");
    }

    const sroCsv = (form.srocodes ?? []).join(","); // empty => ALL

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("Dcode", form.dcode);
      fd.append("TimeHour", String(form.hour));
      fd.append("TimeMin", String(form.min));
      if (sroCsv) fd.append("SrocodeCsv", sroCsv);
      if (form.remark) fd.append("Remark", form.remark);
      if (form.file) fd.append("AttachmentFile", form.file);
      if (updatedBy) fd.append("UpdatedBy", updatedBy);

      const { data } = await axios.post(
        `${API_BASE}/api/SroTime/update_sro_time`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success(data?.message || "SRO time updated");
      setOpen(false); // close popup
      fetchLogs(); // refresh logs on page
      // optional: reset form
      setForm({
        dcode: "",
        srocodes: [],
        hour: 10,
        min: 30,
        remark: "",
        file: null,
      });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || e?.message || "Update failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ────────────────────────────────── Logs ───────────────────────────────────
  const fetchLogs = async () => {
    try {
      setLoadingLogs(true);
      const params = {};
      if (flt.dcode) params.dcode = flt.dcode;
      if (flt.srocode) params.srocode = flt.srocode;
      if (flt.fromDate)
        params.from_date = dayjs(flt.fromDate).format("YYYY-MM-DD");
      if (flt.toDate) params.to_date = dayjs(flt.toDate).format("YYYY-MM-DD");

      const { data } = await axios.get(
        `${API_BASE}/api/SroTime/get_sro_time_logs`,
        { params }
      );
      setLogs(data?.data ?? []);
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 404) setLogs([]);
      else toast.error("Failed to load logs");
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    fetchLogs(); // initial
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────── Table prep ────────────────────────────────
  const tableRows = useMemo(() => {
    return (logs || []).map((r, idx) => ({
      sn: idx + 1,
      when: dayjs(r.logDt).format("DD/MM/YYYY HH:mm"),
      dname: r.dname || r.dcode,
      sroname: r.sroname || r.srocode,
      time: `${String(r.newTimeHour).padStart(2, "0")}:${String(
        r.newTimeMin
      ).padStart(2, "0")}`,
      remark: r.remark || "",
      file_path: r.filePath || "",
      updated_by: r.updatedBy || "",
    }));
  }, [logs]);

  const columns = [
    { name: "sn", label: "S.No", options: { filter: false, sort: true } },
    // { name: "when", label: "When (UTC)", options: { filter: true, sort: true } },
    { name: "dname", label: "District", options: { filter: true, sort: true } },
    { name: "sroname", label: "SRO", options: { filter: true, sort: true } },
    { name: "time", label: "New Time", options: { filter: false, sort: true } },
    {
      name: "remark",
      label: "Remark",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (val) => (
          <div
            style={{
              maxWidth: 280,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {val}
          </div>
        ),
      },
    },
    {
      name: "file_path",
      label: "Document",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (val) =>
          val ? (
            <a
              href={sroViewUrl(val)}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#1976d2" }}
            >
              View
            </a>
          ) : (
            <span style={{ color: "#9e9e9e" }}>No file</span>
          ),
      },
    },
    {
      name: "updated_by",
      label: "Updated By",
      options: { filter: true, sort: true },
    },
  ];

  const tableOptions = {
    selectableRows: "none",
    elevation: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    download: false,
    print: false,
    responsive: "standard",
    textLabels: { body: { noMatch: loadingLogs ? "" : "No records" } },
    customToolbar: () => (
      <Tooltip title="Reload logs">
        <Button onClick={fetchLogs} size="small" variant="outlined">
          Refresh
        </Button>
      </Tooltip>
    ),
  };

  // ───────────────────────────────── Render ──────────────────────────────────
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* ===== Header in page shell ===== */}
      <header className="shadow-sm">
        <Header />
      </header>

      {/* ===== Page body ===== */}
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        {/* Title + New Request button row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            SRO Time Manager
          </Typography>

          <Button
            onClick={() => setOpen(true)}
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            New Request
          </Button>
        </Box>

        {/* Separator */}
        <Divider sx={{ mb: 2 }} />

        {/* ===== Logs Filter + Table card ===== */}
        <Box
          sx={{
            background: "#fff",
            p: 3,
            borderRadius: 2,
            boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            SRO Time Logs
          </Typography>

          {/* Filters */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
              gap: 2,
              mb: 2,
            }}
          >
            {/* District filter */}
            <FormControl
              size="small"
              sx={{ gridColumn: { xs: "auto", md: "span 3" } }}
            >
              <InputLabel>District</InputLabel>
              <Select
                label="District"
                value={flt.dcode}
                onChange={(e) =>
                  setFlt((p) => ({ ...p, dcode: e.target.value }))
                }
              >
                <MenuItem value="">All</MenuItem>
                {districts.map((d) => (
                  <MenuItem key={d.dcode} value={d.dcode}>
                    {d.districtNameEng || d.dname_eng || d.dname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* SRO filter (single) */}
            <FormControl
              size="small"
              sx={{ gridColumn: { xs: "auto", md: "span 3" } }}
              disabled={!flt.dcode}
            >
              <InputLabel>SRO</InputLabel>
              <Select
                label="SRO"
                value={flt.srocode}
                onChange={(e) =>
                  setFlt((p) => ({ ...p, srocode: e.target.value }))
                }
              >
                <MenuItem value="">All</MenuItem>
                {(fltSros || []).map((s) => (
                  <MenuItem key={s.srocode} value={s.srocode}>
                    {s.sroname} ({s.srocode})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Dates */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From"
                value={flt.fromDate}
                onChange={(v) => setFlt((p) => ({ ...p, fromDate: v }))}
                slotProps={{ textField: { size: "small" } }}
                sx={{ gridColumn: { xs: "auto", md: "span 3" } }}
              />
              <DatePicker
                label="To"
                value={flt.toDate}
                onChange={(v) => setFlt((p) => ({ ...p, toDate: v }))}
                slotProps={{ textField: { size: "small" } }}
                sx={{ gridColumn: { xs: "auto", md: "span 3" } }}
              />
            </LocalizationProvider>

            {/* Buttons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                gridColumn: { xs: "auto", md: "span 12" },
              }}
            >
              <Button
                variant="outlined"
                onClick={fetchLogs}
                disabled={loadingLogs}
              >
                {loadingLogs ? <CircularProgress size={18} /> : "Apply Filters"}
              </Button>
              <Button
                variant="text"
                onClick={() =>
                  setFlt({
                    dcode: "",
                    srocode: "",
                    fromDate: null,
                    toDate: null,
                  })
                }
              >
                Clear
              </Button>
            </Box>
          </Box>

          {/* Table */}
          <MUIDataTable
            title={""}
            data={tableRows}
            columns={columns}
            options={tableOptions}
          />
        </Box>
      </Box>

      {/* ===== Popup: New Regularization Request (Update SRO Time) ===== */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
          New Time Request
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 2,
            }}
          >
            {/* Select District */}
            <FormControl size="small" fullWidth>
              <InputLabel>Select District *</InputLabel>
              <Select
                label="Select District *"
                value={form.dcode}
                onChange={(e) =>
                  setForm((p) => ({ ...p, dcode: e.target.value }))
                }
              >
                {districts.map((d) => (
                  <MenuItem key={d.dcode} value={d.dcode}>
                    {d.districtNameEng || d.dname_eng || d.dname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Select SRO (multi) */}
            <FormControl size="small" fullWidth disabled={!form.dcode}>
              <InputLabel>Select SRO *</InputLabel>
              <Select
                multiple
                value={form.srocodes}
                onChange={(e) =>
                  setForm((p) => ({ ...p, srocodes: e.target.value }))
                }
                input={<OutlinedInput label="Select SRO *" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected || []).map((val) => (
                      <Chip key={val} label={val} size="small" />
                    ))}
                  </Box>
                )}
              >
                {sros.map((s) => (
                  <MenuItem key={s.srocode} value={s.srocode}>
                    {s.sroname} ({s.srocode})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Hour */}
            <FormControl size="small" fullWidth>
              <InputLabel>Hour</InputLabel>
              <Select
                label="Hour"
                value={form.hour}
                onChange={(e) =>
                  setForm((p) => ({ ...p, hour: Number(e.target.value) }))
                }
              >
                {hourOptions.map((h) => (
                  <MenuItem key={h} value={h}>
                    {String(h).padStart(2, "0")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Minute */}
            <FormControl size="small" fullWidth>
              <InputLabel>Minute</InputLabel>
              <Select
                label="Minute"
                value={form.min}
                onChange={(e) =>
                  setForm((p) => ({ ...p, min: Number(e.target.value) }))
                }
              >
                {minOptions.map((m) => (
                  <MenuItem key={m} value={m}>
                    {String(m).padStart(2, "0")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Upload */}
            <Button
              variant="outlined"
              component="label"
              fullWidth
              size="small"
              sx={{
                borderStyle: "dashed",
                borderColor: "#1976d2",
                color: "#1976d2",
                fontWeight: 500,
                py: 1.5,
              }}
            >
              {form.file ? form.file.name : "Upload Letter (PDF/JPG/PNG)"}
              <input
                hidden
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) =>
                  setForm((p) => ({ ...p, file: e.target.files?.[0] || null }))
                }
              />
            </Button>

            {/* Remark */}
            <TextField
              label="Remark"
              size="small"
              fullWidth
              placeholder="Reason / Order Ref..."
              value={form.remark}
              onChange={(e) =>
                setForm((p) => ({ ...p, remark: e.target.value }))
              }
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="error"
            sx={{ px: 3 }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ px: 3 }}
            disabled={submitting || !form.dcode}
          >
            {submitting ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Send Request"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
